import { FormInstance } from "antd";
import { IOnSubmitArgs, OpForm } from "components/customAntd/DLS/OpForm/OpForm";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { updateKiosk } from "store/slices/kiosksSlice";
import { fetchLocations } from "store/slices/locationsSlice";
import { AppDispatch, RootState } from "store/store";
import { UpdateKioskPayload } from "types/kioskTypes";
import { hasPermission } from "utils/utils";

interface KioskContentProps {
    kioskForm: FormInstance;
    optionsForm: FormInstance;
    setDefaultLocationId: (locationId: number) => void;
}

const KioskContent: React.FC<KioskContentProps> = ({ kioskForm, optionsForm, setDefaultLocationId }) => {
    const dispatch: AppDispatch = useDispatch();
    const orgId = useSelector((state: RootState) => state.globalOrg.globalOrgId);
    const selectedKiosk = useSelector((state: RootState) => state.kiosks.selectedKiosk);
    const locations = useSelector((state: RootState) => state.locations.locations);

    const tokenScopeList = useSelector((state: RootState) => state.auth.auth.data[0]?.tokenScopeList || []);
    const hasKioskRead = hasPermission(tokenScopeList, orgId, 'o', 'kiosk:r');
    const hasKioskWrite = hasPermission(tokenScopeList, orgId, 'o', 'kiosk:w');

    const { t } = useTranslation();

    useEffect(() => {
        if (orgId && locations.length === 0) {
            dispatch(fetchLocations({ orgId, status: 1 }));
        }
    }, [dispatch, orgId, locations.length]);

    const handleSubmit = async (args: IOnSubmitArgs<UpdateKioskPayload>) => {
        const { values } = args;
        const payload: Partial<UpdateKioskPayload> = {
            name: values.name,
            siteId: values.siteId,
            language: values.language,
        };
        if (selectedKiosk) {
            await dispatch(updateKiosk({ orgId, kioskId: selectedKiosk.id, kiosk: payload }));
        }
    };

    const locationOptions = locations.map(location => ({
        label: location.name,
        value: location.id
    }));

    const defaultLanguageOptions = [
        { label: "English", value: "en-us" },
        { label: "French", value: "fr" },
        { label: "German", value: "de" },
        { label: "Italian", value: "it" },
        { label: "Portuguese", value: "pt" },
        { label: "Spanish", value: "es" },
    ];

    // Check if selectedKiosk's siteId is in the locationOptions
    const validSiteId = locationOptions.some(option => option.value === selectedKiosk?.site.id) ? selectedKiosk?.site.id : undefined;

    const statusStyle = {
        backgroundColor: selectedKiosk?.status.id === 1 ? "#6D988A" : undefined,
        color: selectedKiosk?.status.id === 1 ? "#FFFFFF" : undefined,
    };

    const statusKeyMap: Record<string, string> = {
        'Connected': 'kiosk.status.connected',
        'Not Connected': 'kiosk.status.notConnected',
    };



    return (
        <OpForm
            form={kioskForm}
            onSubmit={handleSubmit}
            hasError={false}
            defaultButtons={false}
            initialValues={{
                name: selectedKiosk?.label,
                status: selectedKiosk?.status.name ? t(statusKeyMap[selectedKiosk.status.name] || selectedKiosk.status.name) : '',
                siteId: validSiteId, // Use the validSiteId
                language: selectedKiosk?.language || 'en-us',
            }}
            isReadOnly={!hasKioskWrite && hasKioskRead}
        >
            <OpForm.Input label={t('kiosk.form.name')} name="name" rules={[{ required: true, message: t('kiosk.form.nameRequired') }]} />

            <OpForm.Input label={t('kiosk.form.status')} name="status" disabled style={statusStyle} />

            <OpForm.Select
                name="siteId"
                label={t('kiosk.form.location')}
                options={locationOptions}
                disabled={selectedKiosk?.status.id === 1}
                onChange={(option) => {
                    setDefaultLocationId(Number(option));
                    // Get the current selected locations from the form
                    const currentValues = optionsForm.getFieldValue('locations') || [];

                    // Filter out the selected option from the current values
                    const validValues = currentValues.filter(
                        (value: any) => value !== Number(option) // Ensure the selected option is removed
                    );

                    // Update the form values with the filtered selections
                    optionsForm.setFieldsValue({ locations: validValues });

                }} />

            <OpForm.Select
                name="language"
                label={t('kiosk.form.defaultLanguage')}
                options={defaultLanguageOptions}
                disabled={!hasKioskWrite}
            />
        </OpForm>
    );
};

export default KioskContent;
